/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Event, eventsApi, EventsArgs, EventUserStatus } from '../services';
import {
  eventCancel,
  eventCancelParticipation,
  eventDelete,
  eventEdit,
  eventJoinAsParticipant,
  eventStatusUpdate,
  eventUpdateChannels,
} from './common';

interface EventsFeedState {
  isFetched: boolean;
  eventsFeed: Event[];
  page: number;
  hasNextPage: boolean;
}

const initialState: EventsFeedState = {
  isFetched: false,
  eventsFeed: [],
  page: 0,
  hasNextPage: false,
};

const getEventsFeedHandler = async (args: EventsArgs, { dispatch }: any) => {
  return dispatch(eventsApi.endpoints.events.initiate({ ...args })).unwrap();
};

export const getEventsFeed = createAsyncThunk('eventsFeed', getEventsFeedHandler);

export const loadMoreEventsFeed = createAsyncThunk('eventsFeed/loadMore', getEventsFeedHandler);

const eventsFeedSlice = createSlice({
  name: 'eventsFeed',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEventsFeed.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getEventsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.isFetched = true;
        state.eventsFeed = items;
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(loadMoreEventsFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.eventsFeed.push(...items);
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(eventJoinAsParticipant.fulfilled, (state, action) => {
        const { eventId, payload, userProfile } = action.payload;

        const event = state.eventsFeed.find(({ id }) => id === eventId);

        if (!event) {
          return;
        }

        if (payload === EventUserStatus.CONFIRMED) {
          event.participantCountConfirmed++;
          event.participantsPreview.push(userProfile);
        } else if (payload === EventUserStatus.WAITLISTED) {
          event.participantCountWaitlisted++;
        }

        event.participantStatus = payload;
      })
      .addCase(eventCancelParticipation.fulfilled, (state, action) => {
        const { eventId, payload, userProfile } = action.payload;

        if (!payload) {
          return;
        }

        const event = state.eventsFeed.find(({ id }) => id === eventId);

        if (!event) {
          return;
        }

        const { participantStatus } = event;

        if (participantStatus === EventUserStatus.CONFIRMED) {
          event.participantCountConfirmed--;
          event.participantsPreview = event.participantsPreview.filter(
            ({ id }) => id !== userProfile.id
          );
        } else if (participantStatus === EventUserStatus.WAITLISTED) {
          event.participantCountWaitlisted--;
        }

        event.participantStatus = null;
      })
      .addCase(eventCancel.fulfilled, (state, action) => {
        const { id, status } = action.payload;

        const event = state.eventsFeed.find((event) => event.id === id);

        if (!event) {
          return;
        }

        event.status = status;
        event.participantStatus = null;
        event.participantCountConfirmed = 0;
        event.participantCountWaitlisted = 0;
        event.participantsPreview = [];
      })
      .addCase(eventStatusUpdate.fulfilled, (state, action) => {
        const { id, status, scheduledAt } = action.payload;

        const event = state.eventsFeed.find((event) => event.id === id);

        if (!event) {
          return;
        }

        event.status = status;
        event.scheduledAt = scheduledAt;
      })
      .addCase(eventEdit.fulfilled, (state, action) => {
        const { id, channels } = action.payload;
        const { channelId } = action.meta.arg;

        if (!channels.some(({ id }: { id: number }) => id === channelId)) {
          state.eventsFeed = state.eventsFeed.filter((event) => event.id !== id);
          return;
        }

        const index = state.eventsFeed.findIndex((event) => event.id === id);

        if (index === -1) {
          return;
        }

        state.eventsFeed[index] = action.payload;
      })
      .addCase(eventDelete.fulfilled, (state, action) => {
        const { eventId, deleted } = action.payload;

        if (!deleted) {
          return;
        }

        state.eventsFeed = state.eventsFeed.filter(({ id }) => id !== eventId);
      })
      .addCase(eventUpdateChannels.fulfilled, (state, action) => {
        const { id, channels } = action.payload;
        const { channelId } = action.meta.arg;

        const currentChannel = channels.find(({ id }: { id: number }) => id === channelId);

        if (!currentChannel) {
          state.eventsFeed = state.eventsFeed.filter((event) => event.id !== id);
          return;
        }

        const event = state.eventsFeed.find((event) => event.id === id);

        if (event) {
          event.channels = channels;
        }
      });
  },
});

export default eventsFeedSlice.reducer;
